import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";

// Data contracts matching Admin design
// Resource: { type: "video" | "pdf", url: string, title?: string }
// Lesson: { _id, title, subtitle?, description?, dayNumber: number, resources: Resource[] }
// Module: { _id, title, order: number, lessons: Lesson[] }
// Course: { _id, title, shortDescription?, modules: Module[] }

// Expect course data to be provided; default is empty shape
const emptyCourse = { _id: "", title: "", shortDescription: "", modules: [] };

const isYouTube = (url) => /youtube\.com|youtu\.be/.test(url);
const toEmbedUrl = (url) => {
  try {
    if (!isYouTube(url)) return url;
    const u = new URL(url);
    // Already an embed
    if (u.hostname.includes('youtube.com') && u.pathname.startsWith('/embed/')) return url;
    // youtu.be/VIDEO_ID
    if (u.hostname === 'youtu.be') {
      const id = u.pathname.replace(/^\//, '');
      return `https://www.youtube.com/embed/${id}`;
    }
    // youtube.com/watch?v=VIDEO_ID or with extra params
    if (u.hostname.includes('youtube.com')) {
      const id = u.searchParams.get('v');
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
  } catch {}
  return url;
};

export default function CoursePlayer({ course = emptyCourse }) {
  const params = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(course);
  const [loading, setLoading] = useState(true);

  // Hide 3rd-party chat widget (Tawk) on course player page to avoid double buttons and distraction
  useEffect(() => {
    const api = window.Tawk_API;
    const hide = () => {
      try { api?.hideWidget?.(); } catch {}
    };
    const show = () => {
      try { api?.showWidget?.(); } catch {}
    };

    let cleared = false;
    const apply = () => {
      if (!cleared) hide();
    };

    // Force-hide via CSS in case API timing fails or widget mounts inside containers
    const style = document.createElement('style');
    style.id = 'hide-tawk-on-course-player';
    style.textContent = `
      /* Hide common Tawk elements on Course Player only */
      .tawk-button,
      .tawk-min-chat-icon,
      .tawk-custom-color,
      [class*="tawk-"][role="button"],
      [class*="tawk-"][aria-label*="Chat"],
      [class*="tawk-"][title*="Chat"],
      [class*="tawk-"][data-tawk-id] {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
    `;
    try { document.head.appendChild(style); } catch {}

    // Aggressive DOM hider: hide any Tawk elements/iframes if they (re)appear
    const hideTawkDom = () => {
      try {
        const selectors = [
          'iframe[src*="tawk.to"]',
          'iframe[title*="tawk" i]',
          '[id^="tawk" i]',
          '[class*="tawk-" i]',
          '[aria-label*="chat" i]',
          '[title*="chat" i]',
          '[data-tawk-id]'
        ];
        const nodes = document.querySelectorAll(selectors.join(', '));
        nodes.forEach((n) => {
          try {
            // More robust check to avoid React DOM conflicts
            if (n && n.parentNode && document.body.contains(n) && !n._reactInternalFiber) {
              // Use requestAnimationFrame to avoid conflicts with React's render cycle
              requestAnimationFrame(() => {
                try {
                  if (n.parentNode && document.body.contains(n)) {
                    n.parentNode.removeChild(n);
                  }
                } catch {}
              });
            }
          } catch {}
        });
      } catch {}
    };
    hideTawkDom();
    
    // Use a more conservative observer that doesn't conflict with React
    let observerTimeout;
    const observer = new MutationObserver((mutations) => {
      // Only process mutations that are not from React components
      const shouldProcess = mutations.some(mutation => {
        // Skip if mutation is from React components (has React fiber)
        const target = mutation.target;
        if (target && target._reactInternalFiber) return false;
        if (target && target.__reactInternalInstance) return false;
        // Skip if target is inside a React component
        if (target && target.closest('[data-reactroot]')) return false;
        return true;
      });
      
      if (shouldProcess) {
        clearTimeout(observerTimeout);
        observerTimeout = setTimeout(hideTawkDom, 200);
      }
    });
    try { 
      observer.observe(document.body, { 
        childList: true, 
        subtree: true,
        attributes: false,
        characterData: false
      }); 
    } catch {}

    // Prevent re-show while on this page
    try {
      if (window.Tawk_API) {
        window.Tawk_API.showWidget = function noop() {};
        window.Tawk_API.maximize = function noop() {};
        window.Tawk_API.toggle = function noop() {};
      }
    } catch {}

    if (api && typeof api.onLoad === "function") {
      api.onLoad(() => { if (!cleared) apply(); });
      apply();
    } else {
      const t = setInterval(() => {
        const ready = window.Tawk_API && (window.Tawk_API.hideWidget || window.Tawk_API.showWidget);
        if (ready) {
          clearInterval(t);
          apply();
          try {
            window.Tawk_API.showWidget = function noop() {};
            window.Tawk_API.maximize = function noop() {};
            window.Tawk_API.toggle = function noop() {};
          } catch {}
        }
      }, 200);
      setTimeout(() => clearInterval(t), 4000);
    }

    return () => {
      cleared = true;
      // Restore default behavior for other pages
      try { show(); } catch {}
      try {
        const s = document.getElementById('hide-tawk-on-course-player');
        if (s && s.parentNode) s.parentNode.removeChild(s);
      } catch {}
      try { 
        clearTimeout(observerTimeout);
        observer.disconnect(); 
      } catch {}
    };
  }, []);

  useEffect(() => {
    const id = params.courseId;
    if (!id || id.startsWith(":")) return; // guard bad route like literal :courseId
    const fetchData = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`/api/app/courses/${id}/player`);
        console.log(data)
        setData(data);
        // Initialize selection by module (each module is a Day)
        const orderedModules = (data.modules || []).slice().sort((a,b)=> (a.order||0)-(b.order||0));
        const firstModule = orderedModules[0];
        const firstLesson = (firstModule?.lessons || [])[0] || null;
        if (firstModule && firstLesson) {
          setOpenDay(firstModule.order || 1);
          setActiveLesson(firstLesson);
        }
        // preload completed map
        const all = (data.modules || []).flatMap((m) => m.lessons || []);
        const initCompleted = {};
        all.forEach((l) => {
          if (l.completed) initCompleted[l._id] = true;
        });
        if (Object.keys(initCompleted).length) setCompleted(initCompleted);
      } catch (e) {}
      setLoading(false);
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.courseId]);

  const days = useMemo(() => {
    const modules = (data.modules || [])
      .slice()
      .sort((a, b) => (a.order || 0) - (b.order || 0));
    
    // If course has no durationDays, show modules directly without day structure
    if (!data.durationDays) {
      return modules.map((m, idx) => ({
        day: m.order || idx + 1,
        title: m.title,
        lessons: (m.lessons || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0)),
        isModule: true, // Flag to indicate this is a module, not a day
      }));
    }
    
    // Original day-based structure
    return modules.map((m, idx) => ({
      day: m.order || idx + 1,
      title: m.title,
      lessons: (m.lessons || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0)),
    }));
  }, [data]);

  const [openDay, setOpenDay] = useState(days[0]?.day ?? null);
  const [activeLesson, setActiveLesson] = useState(days[0]?.lessons[0] ?? null);
  const [completed, setCompleted] = useState({});
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const progress = useMemo(() => {
    const total = (data.modules || []).flatMap((m) => m.lessons || []).length || 1;
    const done = Object.values(completed).filter(Boolean).length;
    return Math.round((done / total) * 100);
  }, [completed, data]);

  const markCompleted = async (lessonId, val) => {
    // Optimistic update
    setCompleted((s) => ({ ...s, [lessonId]: val }));
    try {
      await axios.post("/api/app/progress", { lessonId, completed: !!val });
    } catch (e) {
      // Revert on error
      setCompleted((s) => ({ ...s, [lessonId]: !val }));
    }
  };

  return (
    <div className="h-screen flex flex-col" style={{ height: 'calc(100vh - 120px)' }}>
      <div className="p-3 md:p-6 flex-1 flex flex-col min-h-0">
        <div className={`grid gap-6 lg:gap-8 flex-1 min-h-0 transition-all duration-300 ${
          isSidebarCollapsed 
            ? 'grid-cols-1' 
            : 'grid-cols-1 lg:grid-cols-[360px,1fr]'
        }`}>
        <aside className={`bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden flex flex-col min-h-0 transition-all duration-300 ${
          isSidebarCollapsed ? 'hidden lg:flex' : 'flex'
        }`}>
          {/* Mobile header with collapse button */}
          <div className="lg:hidden p-4 border-b flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsSidebarCollapsed(true)}
                className="p-2 rounded-lg border hover:bg-gray-50 transition-colors"
                aria-label="Hide lessons"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div>
                <h2 className="font-semibold text-sm">Lessons</h2>
                <p className="text-xs text-gray-500">{progress}% completed</p>
              </div>
            </div>
          </div>
          <div className="p-4 border-b hidden lg:block">
            <h1 className="text-lg text-left font-semibold leading-tight">{data.title}</h1>
            {data.shortDescription && (
              <p className="text-sm text-gray-500 mt-1">{data.shortDescription}</p>
            )}
            <div className="mt-3">
              <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full  bg-gray-900 transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="mt-1 text-xs text-gray-500 text-left">{progress}% completed</div>
            </div>
          </div>

          <div className="divide-y overflow-y-auto flex-1 min-h-0">
            {days.map(({ day, title, lessons }) => {
              const open = openDay === day;
              return (
                <div key={day}>
                  <button
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50"
                    onClick={() => setOpenDay(open ? null : day)}
                  >
                    <span className="font-medium">
                      {days[0]?.isModule ? `${title}` : `Day ${day}${title ? ` – ${title}` : ""}`}
                    </span>
                    <svg
                      className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {open && (
                    <ul className="px-2 pb-2 space-y-1">
                      {lessons.map((l) => {
                        const isActive = activeLesson?._id === l._id;
                        const isDone = completed[l._id];
                        return (
                          <li key={l._id}>
                            <button
                              className={`w-full flex items-start gap-2 rounded-lg px-2 py-2 text-left hover:bg-gray-50 ${
                                isActive ? "bg-gray-50" : ""
                              }`}
                              onClick={() => setActiveLesson(l)}
                            >
                              <span
                                className={`mt-0.5 inline-flex h-4 w-4 rounded-full border ${
                                  isDone ? "bg-gray-900 border-gray-900" : "border-gray-300"
                                }`}
                              />
                              <div className="min-w-0">
                                <div className="text-sm font-medium truncate">{l.title}</div>
                                {l.subtitle && (
                                  <div className="text-xs text-gray-500 truncate">{l.subtitle}</div>
                                )}
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {(l.resources || []).map((r, i) => (
                                    <span
                                      key={i}
                                      className={`px-1.5 py-0.5 text-[10px] rounded-full border ${
                                        r.type === "video"
                                          ? "bg-blue-50 text-blue-700 border-blue-200"
                                          : "bg-amber-50 text-amber-700 border-amber-200"
                                      }`}
                                    >
                                      {r.type.toUpperCase()}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </aside>

        <main className="bg-white rounded-2xl shadow-sm flex flex-col min-h-0">
          {/* Mobile collapse button */}
          <div className="lg:hidden p-4 border-b flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                className="p-2 rounded-lg border hover:bg-gray-50 transition-colors"
                aria-label={isSidebarCollapsed ? "Show lessons" : "Hide lessons"}
              >
                <svg
                  className={`w-5 h-5 transition-transform ${isSidebarCollapsed ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div>
                <h2 className="font-semibold text-sm">Lessons</h2>
                <p className="text-xs text-gray-500">{progress}% completed</p>
              </div>
            </div>
            {activeLesson && (
              <div className="text-right">
                <p className="text-sm font-medium truncate max-w-[150px]">{activeLesson.title}</p>
              </div>
            )}
          </div>
          {loading ? (
            <div className="p-10 flex flex-col items-center justify-center gap-3 text-gray-500">
              <svg className="animate-spin h-6 w-6 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
              </svg>
              <div>Loading lessons...</div>
            </div>
          ) : activeLesson ? (
            <>
              <div className="p-4 border-b flex items-center justify-between gap-3">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!completed[activeLesson._id]}
                    onChange={(e) => markCompleted(activeLesson._id, e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  Mark as done
                </label>
              </div>

              <div className="p-4 space-y-6 flex-1 overflow-y-auto min-h-0">
                {(activeLesson.resources || []).map((r, idx) => (
                  <div key={idx} className="space-y-2">
                    <div className="flex items-center gap-2">
                      {r.title && <span className="text-sm text-gray-600">{r.title}</span>}
                    </div>

                    {r.type === "video" ? (
                      <div className="aspect-video w-full overflow-hidden rounded-lg bg-black/5">
                        <iframe
                          className="h-full w-full"
                          src={toEmbedUrl(r.url)}
                          title={`video-${idx}`}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                        />
                      </div>
                    ) : (
                      <div className="w-full h-[72vh] rounded-lg overflow-hidden border">
                        <iframe className="w-full h-full" src={r.url} title={`pdf-${idx}`} />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="p-4 border-t flex items-center justify-between flex-shrink-0">
                <button
                  className="px-3 py-2 rounded-lg border hover:bg-gray-50"
                  onClick={() => {
                    const flat = (data.modules || [])
                      .slice()
                      .sort((a,b)=> (a.order||0)-(b.order||0))
                      .flatMap((m) => (m.lessons || []).slice().sort((a,b)=> (a.order||0)-(b.order||0)));
                    const idx = flat.findIndex((l) => l._id === activeLesson._id);
                    if (idx > 0) setActiveLesson(flat[idx - 1]);
                  }}
                >
                  ← Previous
                </button>
                <button
                  className="px-3 py-2 rounded-lg bg-gray-900 text-white hover:bg-black"
                  onClick={() => {
                    const flat = (data.modules || [])
                      .slice()
                      .sort((a,b)=> (a.order||0)-(b.order||0))
                      .flatMap((m) => (m.lessons || []).slice().sort((a,b)=> (a.order||0)-(b.order||0)));
                    const idx = flat.findIndex((l) => l._id === activeLesson._id);
                    if (idx < flat.length - 1) setActiveLesson(flat[idx + 1]);
                  }}
                >
                  Next →
                </button>
              </div>
            </>
          ) : (
            <div className="p-10 text-center text-gray-500">Pick a lesson to start learning.</div>
          )}
        </main>
        </div>
      </div>
    </div>
  );
}


